// MCP Security — Button styles.
//
// Thin re-export over the WAF event-drawer shared primitives
// (src/components/pages/_new/detection/wafEventDrawerShared.js) so the
// operator-facing MCP pages share the exact same button + colour
// language as the WAF Events drawer. Single source of truth: the WAF
// file.
//
// Names re-exported verbatim: primaryButtonStyle, secondaryButtonStyle,
// PRIMARY_COLOR, TEXT_PRIMARY, BORDER_COLOR. The ten+ MCP call sites
// keep their existing `import { ... } from "./_shared/buttonStyles"`
// lines untouched.
//
// Names with no WAF-side equivalent (dangerButtonStyle, iconButtonStyle,
// sectionCardSx, sectionCardTitleSx, tableHeadSx, tableBodySx) stay
// defined locally — WAF renders the equivalent surfaces through the
// SmartParseSectionCard component rather than exposing sx primitives,
// so there's nothing to re-export for them yet.

import tokens from "./tokens";

export {
  primaryButtonStyle,
  secondaryButtonStyle,
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  BORDER_COLOR,
} from "../../../detection/wafEventDrawerShared";

const LOCAL_TEXT_PRIMARY = "#1A1A1A";
const LOCAL_BORDER_COLOR = "#E5E7EB";

const baseFontStyle = {
  fontSize: 14,
  lineHeight: "30px",
};

// no WAF equivalent — destructive variant local to MCP (revoke session,
// block agent, delete endpoint). WAF event drawer only ships primary +
// secondary.
export const dangerButtonStyle = {
  ...baseFontStyle,
  height: 36,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  backgroundColor: "#fff",
  border: `1px solid ${LOCAL_BORDER_COLOR}`,
  color: tokens.danger[600],
  "&:hover": {
    backgroundColor: "rgba(251,55,72,0.06)",
    borderColor: tokens.danger[600],
    color: tokens.danger[600],
  },
};

// no WAF equivalent — icon-only round button (close affordance in
// drawers, etc.).
export const iconButtonStyle = {
  width: 36,
  height: 36,
  borderRadius: "8px",
  color: tokens.neutral[700],
  "&:hover": {
    backgroundColor: "rgba(40, 46, 100, 0.055)",
    color: LOCAL_TEXT_PRIMARY,
  },
};

// no WAF equivalent — section-card wrapper sx. WAF uses the
// SmartParseSectionCard component for the same visual role; MCP pages
// (Overview, Config) consume this as a plain sx for Box wrappers.
export const sectionCardSx = {
  background: "#fff",
  border: `1px solid ${tokens.neutral[200]}`,
  borderRadius: tokens.cardRadius,
  p: 2,
};

// no WAF equivalent — section-card title typography. Values match the
// WAF card title typography; kept local so Overview/Config can style
// Typography directly without the full SmartParseSectionCard shell.
export const sectionCardTitleSx = {
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: "medium",
  color: tokens.neutral[1000],
};

// no WAF equivalent — table-header cell typography. Same values as the
// WAF Events table header; WAF renders through its own table primitives.
export const tableHeadSx = {
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: "medium",
  color: tokens.neutral[1000],
};

// no WAF equivalent — table-body cell typography.
export const tableBodySx = {
  fontSize: 14,
  lineHeight: "21px",
  color: tokens.neutral[800],
};
